import axios from "axios";

const api = axios.create({
  baseURL: import.meta.env.VITE_BACKEND_URL,
  withCredentials: true,
});
// Separate client so refresh request doesn't trigger the 401 interceptor
const refreshApi = axios.create({
  baseURL: import.meta.env.VITE_BACKEND_URL,
  withCredentials: true,
});
let isRefreshing = false;
type RefreshQueueEntry = {
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
};
let queue: RefreshQueueEntry[] = [];
const processQueue = (error: unknown, token: string | null = null) => {
  queue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      if (token) resolve(token);
    }
  });
  queue = [];
};
const logout = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("verified");
  window.dispatchEvent(new Event("forceLogout"));
};
/*
 * Attach access token to every request.
 */
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);
/*
 * Handle API responses.
 */
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const request = error.config;
    // Only handle 401 responses
    if (error.response?.status !== 401 || !request) {
      return Promise.reject(error);
    }
    // Don't retry the same request twice
    if (request._retry) {
      return Promise.reject(error);
    }
    /*
     * If another request is already refreshing,
     * wait for that refresh to finish.
     */
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        queue.push({ resolve, reject });
      }).then((token) => {
        request.headers.Authorization = `Bearer ${token}`;

        return api(request);
      });
    }
    /*
     * This request handles the refresh.
     */
    request._retry = true;
    isRefreshing = true;
    try {
      const { data } = await refreshApi.post("/user/auth/refresh-token");
      const newToken = data.accessToken;
      if (!newToken) {
        throw new Error("No access token returned");
      }
      // Store new access token
      localStorage.setItem("accessToken", newToken);
      // Resolve all waiting requests
      processQueue(null, newToken);
      // Retry original request
      request.headers.Authorization = `Bearer ${newToken}`;
      return api(request);
    } catch (refreshError) {
      // Reject all waiting requests
      processQueue(refreshError);
      // Authentication has failed
      logout();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export default api;
